import { useEffect, useState } from "react";
import { Sunrise, BookOpen, Feather, Flame, Home as HomeIcon, PenLine, Moon, Sparkles, Lock, Users, Check, PlayCircle, Headphones, Download, Gift } from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuth";
import { portalApi, apiError } from "@/lib/portalApi";
import MediaAttach from "./MediaAttach";
import MediaGrid from "./MediaGrid";
import { toast } from "sonner";

const now = new Date();
const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
const pretty = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

function Block({ icon: Icon, label, title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#A9724E]" strokeWidth={1.5} />
        <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">{label}</p>
      </div>
      {title && <h2 className="font-serif-display text-2xl md:text-3xl text-[#2A2825] mb-3">{title}</h2>}
      <div className="text-[#5E4B3C] font-body leading-relaxed">{children}</div>
    </section>
  );
}

function TeachingVideo({ url }) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (yt) return <iframe title="teaching" data-testid="teaching-video" className="w-full aspect-video rounded-lg" src={`https://www.youtube.com/embed/${yt[1]}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
  if (vimeo) return <iframe title="teaching" data-testid="teaching-video" className="w-full aspect-video rounded-lg" src={`https://player.vimeo.com/video/${vimeo[1]}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />;
  return <video data-testid="teaching-video" src={url} controls className="w-full rounded-lg bg-black" />;
}

function EntryBlock({ type, icon, label, title, prompt, placeholder, week, testid }) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/entries/me", { params: { date: dateStr, type } })
      .then((r) => { const e = r.data[0]; if (e) { setText(e.text || ""); setVisibility(e.visibility || "private"); setMedia(e.media || []); } })
      .catch(() => {});
  }, [type]);

  const save = async () => {
    setSaving(true);
    try {
      await portalApi.post("/portal/entries", { type, date: dateStr, text, visibility, week, media });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast.success(visibility === "cohort" ? "Shared with your cohort." : "Saved — just for you.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Block icon={icon} label={label} title={title} testid={testid}>
      {prompt && <p className="mb-3">{prompt}</p>}
      <textarea data-testid={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={placeholder}
        className="w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E]" />
      <div className="mt-3"><MediaAttach testid={`${testid}-media`} value={media} onChange={setMedia} scope={visibility === "cohort" ? "cohort" : "private"} /></div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="inline-flex rounded-full border border-[#D8CEBE] p-0.5 bg-white">
          <button data-testid={`${testid}-private`} onClick={() => setVisibility("private")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${visibility === "private" ? "bg-[#2A2825] text-[#F4EFE6]" : "text-[#8a7a6a]"}`}>
            <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />Private
          </button>
          <button data-testid={`${testid}-share`} onClick={() => setVisibility("cohort")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${visibility === "cohort" ? "bg-[#A9724E] text-[#F4EFE6]" : "text-[#8a7a6a]"}`}>
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} />Share with cohort
          </button>
        </div>
        <button data-testid={`${testid}-save`} onClick={save} disabled={saving || (!text.trim() && media.length === 0)}
          className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-xs tracking-wide transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
          {saved ? <><Check className="w-3.5 h-3.5" strokeWidth={2} />Saved</> : saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Block>
  );
}

export default function Today() {
  const { user } = usePortalAuth();
  const [week, setWeek] = useState(null);
  const [c, setC] = useState(null);
  const firstName = (user?.name || "friend").split(" ")[0];

  useEffect(() => {
    portalApi.get("/portal/content/current").then((r) => { setWeek(r.data.week); setC(r.data.content); }).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-today" className="max-w-3xl">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">{pretty}{week ? ` · Week ${week}${c?.movement ? " · " + c.movement : ""}` : ""}</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Good morning, {firstName}.</h1>
      <p className="text-[#5E4B3C] font-body mb-10 leading-relaxed">
        {c?.theme ? <>This week the light falls on <span className="text-[#A9724E] italic">{c.theme.toLowerCase()}</span>. Move slowly — there is nothing to finish, only to live.</> : "One day at a time. Move through it slowly — there is nothing to finish, only to live."}
      </p>

      <div className="space-y-5">
        {c?.teaching_video_url && (
          <Block testid="today-teaching" icon={PlayCircle} label="This Week's Teaching" title={c.practice_title ? `On ${c.theme}` : "Teaching"}>
            <TeachingVideo url={c.teaching_video_url} />
            {c.teaching_audio_url && (
              <div className="mt-4">
                <p className="flex items-center gap-2 text-xs tracking-overline uppercase text-[#8a7a6a] mb-2"><Headphones className="w-3.5 h-3.5" strokeWidth={1.5} />Audio only</p>
                <audio data-testid="teaching-audio" src={c.teaching_audio_url} controls className="w-full" />
              </div>
            )}
          </Block>
        )}
        {!c?.teaching_video_url && c?.teaching_audio_url && (
          <Block testid="today-teaching-audio" icon={Headphones} label="This Week's Teaching" title="Audio">
            <audio data-testid="teaching-audio" src={c.teaching_audio_url} controls className="w-full" />
          </Block>
        )}

        <Block testid="today-morning-prayer" icon={Sunrise} label="Morning Prayer" title="Before anything else">
          <p className="font-serif-display italic text-lg text-[#2A2825]">“{c?.prayer || "Tip me over today, Lord. Let what pours out be honest. Empty me of what isn't yours, and fill the space with You."}”</p>
          <p className="mt-3 text-sm text-[#8a7a6a]">Sit with this for a breath before you scroll on.</p>
        </Block>

        <Block testid="today-scripture" icon={BookOpen} label="Today's Scripture" title={c?.scripture_ref || "Jeremiah 48:11"}>
          <p>“{c?.scripture_text || "Moab has been at ease from his youth; he has settled on his dregs; he has not been emptied from vessel to vessel…"}”</p>
        </Block>

        <EntryBlock testid="today-reflection" type="reflection" icon={Feather} label="Reflection"
          prompt={c?.reflection_prompt || "What has settled in you because it's never been poured out?"}
          placeholder="Write what surfaces…" week={week} />

        <Block testid="today-challenge" icon={Flame} label="Today's Practice" title={c?.practice_title || "Serve someone unseen"}>
          <p>{c?.practice_body || "Do one small thing for someone who will never know it was you."}</p>
          <p className="mt-3 text-sm text-[#A9724E]">Mark your practices complete on the Challenges page — this is the invitation.</p>
        </Block>

        <Block testid="today-hospitality" icon={HomeIcon} label="Hospitality">
          <p>{c?.hospitality || "Set one extra place today — a text, a coffee, a door held open longer than comfortable."}</p>
        </Block>

        <EntryBlock testid="today-journal" type="journal" icon={PenLine} label="Journal" title="What are you noticing?" placeholder="Write freely." week={week} />

        <Block testid="today-evening" icon={Moon} label="Evening Reflection">
          <p>Where did you feel most yourself today? Where did you feel most emptied? Both are the work.</p>
        </Block>

        <EntryBlock testid="today-story-harvest" type="story" icon={Sparkles} label="Story Harvest" title="What story is God writing today?"
          placeholder="A sentence is enough. These gather into your testimony over 24 weeks." week={week} />

        {c?.downloads?.length > 0 && (
          <Block testid="today-downloads" icon={Download} label="Downloads & Resources">
            <div className="space-y-2">
              {c.downloads.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#A9724E] hover:underline">
                  <Download className="w-4 h-4" strokeWidth={1.5} />{d.title || d.url}
                </a>
              ))}
            </div>
          </Block>
        )}

        {c?.bonus_unlocked && c?.bonus?.length > 0 && (
          <Block testid="today-bonus" icon={Gift} label="Bonus — unlocked for you">
            <div className="space-y-4">
              {c.bonus.map((b, i) => (
                <div key={i}>
                  <p className="font-serif-display text-xl text-[#2A2825]">{b.title}</p>
                  <p className="mt-1 whitespace-pre-wrap">{b.body}</p>
                </div>
              ))}
            </div>
          </Block>
        )}
      </div>
    </div>
  );
}
